/**
 * SC-209: the window itself, on Chromium and Firefox.
 *
 * This owns everything React cannot: opening the window, dressing its document, holding a wake lock on its
 * behalf, hearing it close, and telling React where to render. React renders *through* it, not inside it —
 * `PipHost` portals into `getSurface().document`, which keeps the whole tree inside the main app's providers.
 * That is why this file has no JSX and creates no root of its own.
 */

import {
  applyStyles,
  collectStyleRules,
  createPipEmotionCache,
  watchSevvVars,
} from '../pipStyleBridge'
import { PIP_BASE_CSS } from '../pipStyles'
import { pipWindowTitle, shouldHoldPipWakeLock } from '../pipModel'
import type { PipModel } from '../pipModel'
import { requestLiveKitAudioResume } from '../../../../utils/livekitAudioResumeRequest'
import { documentPipApi } from './documentPipApi'
import type { PipClosedBy, PipOpenReason, PipRenderer, PipSurface } from './PipRenderer'

/**
 * The window the browser opens first. The user resizes it from there and the browser remembers, so this is a
 * starting point and not a constraint — `pipLayout` reflows to whatever they drag it to.
 */
const PIP_WINDOW_WIDTH = 420
const PIP_WINDOW_HEIGHT = 320

/** SC-257 × SC-209: the gestures that count as "the user touched the window, audio may resume". */
const RESUME_GESTURES = ['pointerdown', 'keydown', 'touchstart'] as const

type WakeLockSentinelLike = {
  release: () => Promise<void>
  addEventListener?: (type: 'release', listener: () => void) => void
}

type WakeLockCapableNavigator = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> }
}

export class DocumentPipRenderer implements PipRenderer {
  private win: Window | null = null
  private surface: PipSurface | null = null
  private opening: Promise<void> | null = null

  /**
   * §2.3: every renderer is handed the model whether or not it is open. This one has no use for it while
   * closed — React feeds the open window directly — but keeping it honours the contract Safari's renderer
   * depends on, and makes "was this renderer kept up to date?" answerable in a test.
   */
  private latestModel: PipModel | null = null

  private readonly changeListeners = new Set<() => void>()
  private readonly closedListeners = new Set<(by: PipClosedBy) => void>()
  private teardowns: Array<() => void> = []

  private wakeLock: WakeLockSentinelLike | null = null
  private acquiringWakeLock = false
  private wakeLockRefused = false

  /** Tells a close we asked for apart from one the user made with the window's own button. */
  private closingOurselves = false

  /** Set when `close()` arrives while `requestWindow()` is still in flight; the window is thrown away. */
  private cancelled = false

  get isOpen(): boolean {
    return this.win !== null
  }

  get model(): PipModel | null {
    return this.latestModel
  }

  async open(model: PipModel, _opts: { reason: PipOpenReason }): Promise<void> {
    this.latestModel = model
    if (this.win) return
    if (this.opening) {
      // Re-opening while a window is still being created: whatever cancelled the previous attempt no longer
      // applies, and leaving the flag set would throw this window away too — resolving as if it had opened,
      // and leaving the toolbar button showing "close" for a window that does not exist.
      this.cancelled = false
      return this.opening
    }

    const api = documentPipApi()
    if (!api) throw new Error('This browser has no Document Picture-in-Picture')

    this.cancelled = false
    this.opening = api
      .requestWindow({ width: PIP_WINDOW_WIDTH, height: PIP_WINDOW_HEIGHT })
      .then((win) => {
        // The room can be left, or the controller unmounted, in the time a window takes to appear. Without
        // this the window opens anyway — dressed, holding a wake lock, and with nothing rendering into it,
        // because `PipHost` has gone. An always-on-top blank window only the user can close.
        if (this.cancelled) {
          win.close()
          return
        }
        this.attach(win)
      })
      .finally(() => {
        this.opening = null
        this.cancelled = false
      })

    return this.opening
  }

  update(model: PipModel): void {
    this.latestModel = model
    // The room and the head count are in the title bar, so they have to follow the model like anything else
    // the window shows.
    this.applyTitle()
  }

  private applyTitle(): void {
    const win = this.win
    const model = this.latestModel
    if (!win || !model) return

    win.document.title = pipWindowTitle({
      // The tab's own title is the only place the application's name lives.
      appName: document.title,
      roomName: model.roomName,
      proximityCount: model.proximityCount,
    })
  }

  close(): void {
    const win = this.win

    // Closing while the window is still being created: there is nothing to close yet, so say so and let the
    // pending `open()` throw the window away when it arrives.
    if (!win) {
      if (this.opening) this.cancelled = true
      return
    }

    this.closingOurselves = true
    win.close()
    // `pagehide` settles this in every browser measured, but a renderer that could be left reporting itself
    // open after a close would strand the toolbar button in the wrong state; `handleClosed` is idempotent.
    this.handleClosed()
  }

  onClosed(cb: (by: PipClosedBy) => void): () => void {
    this.closedListeners.add(cb)
    return () => {
      this.closedListeners.delete(cb)
    }
  }

  subscribe(cb: () => void): () => void {
    this.changeListeners.add(cb)
    return () => {
      this.changeListeners.delete(cb)
    }
  }

  getSurface(): PipSurface | null {
    return this.surface
  }

  private attach(win: Window): void {
    this.win = win
    const doc = win.document

    // Order matters: the application's own rules first, our shell's on top of them, and emotion's cache
    // inserts after both, so a component style still wins over the base — the precedence of the main window.
    applyStyles(doc, collectStyleRules(document))
    const base = doc.createElement('style')
    base.setAttribute('data-sevv-pip', 'base')
    base.textContent = PIP_BASE_CSS
    doc.head.appendChild(base)

    this.teardowns.push(watchSevvVars(document.documentElement, doc.documentElement))

    const emotionCache = createPipEmotionCache(doc)

    this.listen(win, 'resize', () => this.publish(emotionCache))
    this.listen(win, 'pagehide', () => this.handleClosed())

    // SC-257 × SC-209 task 1.13(a): a click in here never reaches the opener's listeners, and with the
    // window open the tab never becomes visible again either — so nothing else would ever un-block audio.
    // Dispatched synchronously in the handler, because that is the only place the gesture still counts.
    for (const type of RESUME_GESTURES) {
      // `force`: these are real gestures inside the window, and the state they have to recover from — a
      // suspended AudioContext behind a backgrounded tab — is one `Room.canPlaybackAudio` cannot see.
      this.listen(doc, type, () => requestLiveKitAudioResume('pip', window, { force: true }))
    }

    // R15: the lock belongs to a document and dies when that document hides, so it is re-asked on every
    // visibility change rather than once on open.
    this.listen(doc, 'visibilitychange', () => {
      void this.evaluateWakeLock()
    })

    this.applyTitle()
    this.publish(emotionCache)
    void this.evaluateWakeLock()
  }

  private listen(target: EventTarget, type: string, handler: EventListener): void {
    target.addEventListener(type, handler)
    this.teardowns.push(() => target.removeEventListener(type, handler))
  }

  private publish(emotionCache: PipSurface['emotionCache']): void {
    const win = this.win
    this.surface = win
      ? {
          window: win,
          document: win.document,
          width: win.innerWidth,
          height: win.innerHeight,
          emotionCache,
        }
      : null
    this.changeListeners.forEach((listener) => listener())
  }

  private handleClosed(): void {
    if (!this.win) return

    const by: PipClosedBy = this.closingOurselves ? 'user' : 'browser'
    this.closingOurselves = false

    const teardowns = this.teardowns
    this.teardowns = []
    for (const teardown of teardowns) teardown()

    void this.releaseWakeLock()
    this.wakeLockRefused = false

    this.win = null
    this.surface = null
    this.changeListeners.forEach((listener) => listener())
    this.closedListeners.forEach((listener) => listener(by))
  }

  /**
   * R15, measured 2026-09-20. Chromium grants this and the display stays awake with the tab in the
   * background. Firefox reports the picture-in-picture document as hidden while its window is plainly on
   * screen, so it refuses; the lock there comes and goes with focus and nothing is lost. Nothing branches on
   * browser, nothing retries in a loop, and a refusal is logged once and forgotten.
   */
  private async evaluateWakeLock(): Promise<void> {
    const win = this.win
    const nav = win?.navigator as WakeLockCapableNavigator | undefined
    const supported = typeof nav?.wakeLock?.request === 'function'

    if (!shouldHoldPipWakeLock({ open: win !== null, supported })) {
      await this.releaseWakeLock()
      return
    }
    if (this.wakeLock || this.acquiringWakeLock) return

    this.acquiringWakeLock = true
    try {
      const sentinel = await nav!.wakeLock!.request('screen')

      if (!this.win) {
        // The window closed while the request was in flight.
        await sentinel.release().catch(() => {})
        return
      }

      this.wakeLock = sentinel
      sentinel.addEventListener?.('release', () => {
        // Forgotten rather than re-requested here: the next visibility change asks again, and a lock that is
        // dropped on every blur (Firefox) must not turn into a request loop.
        if (this.wakeLock === sentinel) this.wakeLock = null
      })
    } catch (error) {
      if (!this.wakeLockRefused) {
        this.wakeLockRefused = true
        console.debug('[PiP] screen wake lock refused; the display may sleep while the window is open', error)
      }
    } finally {
      this.acquiringWakeLock = false
    }
  }

  private async releaseWakeLock(): Promise<void> {
    const sentinel = this.wakeLock
    this.wakeLock = null
    if (!sentinel) return
    try {
      await sentinel.release()
    } catch {
      // Already gone. The browser releases on hide, so losing this race is the ordinary case.
    }
  }
}
